import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Clock, FileText, ChevronRight, UserCheck, UserCog } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";
import { rowActivation } from "@/lib/row-navigation";
import type {
  ApprovalDelegationDto,
  ApprovalQueueItemDto,
  DocumentType,
} from "@procureflow/shared-types";

// Per-document-type queue display (ADR-018, US-058).
const DOC_TYPE_LABEL: Record<DocumentType, string> = {
  PR: "PR",
  PO: "PO",
  RFQ: "RFQ",
  GRN: "GRN",
  Invoice: "Invoice",
};
const DOC_TYPE_ROUTE = {
  PR: "/purchase-requests/$id",
  PO: "/purchase-orders/$id",
  RFQ: "/rfq/$id",
  GRN: "/grn/$id",
  Invoice: "/invoices/$id",
} as const satisfies Record<DocumentType, string>;

// US-017 — Delegate candidate list. `/api/users` is Admin-only per the
// api-contract (US-002, US-004); approvers like Peter cannot fetch it
// to populate this picker. Embedding the seed approver list keeps the
// slice scoped to the FE; a follow-up slice can introduce a contract
// addition (e.g. GET /api/delegations/candidates) that surfaces this
// server-side.
const DELEGATE_CANDIDATES: ReadonlyArray<{ id: string; name: string; role: string }> = [
  { id: "00000000-0000-0000-0000-000000000001", name: "Alice Admin", role: "Admin" },
  { id: "00000000-0000-0000-0000-000000000002", name: "Frank Finance", role: "Finance" },
  { id: "00000000-0000-0000-0000-000000000003", name: "Flora Finance", role: "Finance" },
  {
    id: "00000000-0000-0000-0000-000000000004",
    name: "Peter Procurement",
    role: "ProcurementOfficer",
  },
  {
    id: "00000000-0000-0000-0000-000000000005",
    name: "Patricia Procurement",
    role: "ProcurementOfficer",
  },
];

export const Route = createFileRoute("/approvals")({
  head: () => ({
    meta: [
      { title: "Approvals — ProcureFlow" },
      {
        name: "description",
        content: "Review and act on purchase requests awaiting your approval.",
      },
    ],
  }),
  component: ApprovalsPage,
});

// US-061 — no currency symbol; grouped 2-decimal (matches @/lib/format).
function formatAmount(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatSLA(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoPlusDays(days: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function ManageDelegationPanel({ actorId }: { actorId: string }) {
  const [active, setActive] = useState<ApprovalDelegationDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [delegateId, setDelegateId] = useState("");
  const [startDate, setStartDate] = useState(isoToday());
  const [endDate, setEndDate] = useState(isoPlusDays(7));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchOwn() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth("/api/delegations");
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setError(body.message ?? "Failed to load delegation.");
          return;
        }
        const { data } = (await res.json()) as { data: ApprovalDelegationDto[] };
        if (!cancelled) setActive(data[0] ?? null);
      } catch {
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchOwn();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!delegateId) {
      setError("Please choose a delegate.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithAuth("/api/delegations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ delegate_id: delegateId, start_date: startDate, end_date: endDate }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.message ?? "Failed to create delegation.");
        return;
      }
      const { data } = (await res.json()) as { data: ApprovalDelegationDto };
      setActive(data);
      setDelegateId("");
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel() {
    if (!active) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetchWithAuth(`/api/delegations/${active.id}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) {
        const body = await res.json().catch(() => ({}));
        setError(body.message ?? "Failed to cancel delegation.");
        return;
      }
      setActive(null);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6">
        <div className="h-5 w-32 rounded bg-muted/40 animate-pulse" />
      </div>
    );
  }

  const candidates = DELEGATE_CANDIDATES.filter((c) => c.id !== actorId);

  return (
    <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-4">
      <div>
        <h2 className="text-sm font-semibold text-foreground">Approval delegation</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Route your incoming approval requests to a delegate during a date range.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      {active ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
            <p className="text-sm font-medium">Active delegation to {active.delegate.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {active.start_date} – {active.end_date}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40 disabled:opacity-50"
          >
            Cancel delegation
          </button>
        </div>
      ) : (
        <form onSubmit={handleCreate} className="space-y-3">
          <p className="text-xs text-muted-foreground">No active delegation.</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="space-y-1 text-xs">
              <span className="block text-muted-foreground">Delegate</span>
              <select
                aria-label="Delegate"
                value={delegateId}
                onChange={(e) => setDelegateId(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
              >
                <option value="">Select a user…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.role})
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1 text-xs">
              <span className="block text-muted-foreground">Start date</span>
              <input
                aria-label="Start date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
            <label className="space-y-1 text-xs">
              <span className="block text-muted-foreground">End date</span>
              <input
                aria-label="End date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
              />
            </label>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-glow disabled:opacity-50"
          >
            Create delegation
          </button>
        </form>
      )}
    </div>
  );
}

function ApprovalsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<ApprovalQueueItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchQueue() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth("/api/approval-queue?limit=50");
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setError(body.message ?? "Failed to load approval queue.");
          return;
        }
        const { data, meta } = await res.json();
        if (!cancelled) {
          setItems(data);
          setTotal(meta.total);
        }
      } catch {
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchQueue();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (user?.role === "Requester") {
    return (
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
        <p className="text-sm font-medium text-foreground">Access restricted</p>
        <p className="text-sm text-muted-foreground">
          The approval queue is only available to approvers.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Approval Queue</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {total === 0
              ? "No items awaiting your action."
              : `${total} item${total !== 1 ? "s" : ""} awaiting your action.`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPanelOpen((open) => !open)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
        >
          <UserCog className="h-3.5 w-3.5" /> Manage delegation
        </button>
      </div>

      {panelOpen && user && <ManageDelegationPanel actorId={user.id} />}

      {items.length === 0 ? (
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-12 text-center space-y-3">
          <div className="h-12 w-12 rounded-full bg-success/10 grid place-items-center mx-auto">
            <UserCheck className="h-6 w-6 text-[var(--success)]" />
          </div>
          <p className="text-sm font-medium text-foreground">You're all caught up</p>
          <p className="text-sm text-muted-foreground">
            No purchase requests are waiting for your approval.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left font-medium px-5 py-3">Document</th>
                  <th className="text-left font-medium px-3 py-3">Type</th>
                  <th className="text-left font-medium px-3 py-3">Submitted by</th>
                  <th className="text-right font-medium px-3 py-3">Value</th>
                  <th className="text-left font-medium px-3 py-3">Submitted</th>
                  <th className="text-left font-medium px-3 py-3">SLA</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.approval_step_id}
                    className="border-t border-border cursor-pointer hover:bg-muted/30 transition-colors"
                    {...rowActivation(() =>
                      navigate({
                        to: DOC_TYPE_ROUTE[item.document_type],
                        params: { id: item.document_id },
                      }),
                    )}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="h-7 w-7 rounded-lg bg-primary/10 grid place-items-center text-primary shrink-0">
                          <FileText className="h-3 w-3" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate max-w-[200px]">
                            {item.title || item.document_number}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {item.document_number}
                          </div>
                        </div>
                      </div>
                      {item.delegated_from && (
                        <div className="mt-1 ml-9 text-xs text-muted-foreground">
                          delegated from {item.delegated_from.name}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <span className="inline-flex items-center rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium text-foreground">
                        {DOC_TYPE_LABEL[item.document_type]}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                      {item.submitted_by}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">
                      {item.total_value === null ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        formatAmount(item.total_value)
                      )}
                    </td>
                    <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">
                      {formatDate(item.submitted_at)}
                    </td>
                    <td className="px-3 py-3">
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-medium ${
                          item.sla_elapsed_hours > 48
                            ? "text-destructive"
                            : item.sla_elapsed_hours > 24
                              ? "[color:var(--warning)]"
                              : "text-muted-foreground"
                        }`}
                      >
                        <Clock className="h-3 w-3" />
                        {formatSLA(item.sla_elapsed_hours)}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <Link
                        to={DOC_TYPE_ROUTE[item.document_type]}
                        params={{ id: item.document_id }}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium whitespace-nowrap"
                      >
                        Review <ChevronRight className="h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
